// Точность синастрии: чем расчёт РАСПОЛАГАЛ, а не что человек выбрал в форме.
//
// Правило 7 CLAUDE.md: доменные формулы живут в контрактах и импортируются обеими сторонами.
// Классификация точности — тоже формула: от неё зависит, на какой момент строятся карты,
// участвует ли асцендент и какую оговорку обязан произнести разбор. Расходиться фронту и бэку
// тут нельзя — иначе интерфейс снова начнёт обещать не то, что посчитано.
import type { CompatPrecision } from '../enums.js'

/**
 * Версия математики совместимости.
 *
 * v1 — обе карты строились на полдень UTC, время и город не участвовали вовсе.
 * v2 — карты строятся на точный момент рождения по исторической зоне города, при полных
 *      данных обеих сторон в расчёт входит асцендент.
 * v5 — наборы пар во всех линзах стали зеркальными: до этого результат зависел от того,
 *      кто нажал кнопку, и две подруги по одной линзе видели разные числа.
 * v4 — общий процент стал средневзвешенным по шкалам линзы, асцендент вошёл отдельной
 *      шкалой, а к переводу в проценты добавлена линейная растяжка. До этого итог и шкалы
 *      считались по разным парам точек и спокойно спорили друг с другом на экране.
 * v3 — шкала растянута на 30..100. Сам способ считать прежний, синастрия та же, но обе
 *      составляющие теперь нормируются по своим настоящим границам, а сглаживание углов
 *      меряется в средних парах, а не в сырых произведениях весов. До этого расчёт физически
 *      не выдавал ничего ниже 59, и половина ступеней приговора была недостижима.
 *
 * Версия входит в ключ кэша (`seed`), поэтому проверка, посчитанная старой формулой, никогда
 * не отдаётся как ответ на новый запрос: старая запись остаётся в истории с тем числом, которое
 * человеку показали, а новая проверка создаёт новую строку. Молча переписывать уже показанный
 * результат нельзя — это то же самое, что подделать историю.
 */
export const COMPAT_CALC_VERSION = 5

/** Порядок от точного к грубому — им сравниваются две стороны пары. */
const PRECISION_RANK: Record<CompatPrecision, number> = {
  exact: 0,
  time_no_city: 1,
  date_only: 2,
  sign_only: 3,
}

export interface CompatSideData {
  /** Известна полная дата рождения. `false` — режим «только знак». */
  hasDate: boolean
  hasTime: boolean
  /** Город рождения — а значит и его координаты с исторической IANA-зоной. */
  hasCity: boolean
}

/**
 * Точность одной стороны.
 *
 * Время без города — отдельная ступень, а не «полные данные»: перевести местное время в UTC
 * без зоны нельзя, и мы подставляем зону по умолчанию. Для планет это допущение стоит часов
 * (Луна — до ~5°), для асцендента оно стоило бы целого знака, поэтому асцендент там не считается.
 * Ровно так же ведёт себя натальная карта (`apps/api/src/astro/natal.ts`).
 */
export const resolveCompatPrecision = ({
  hasDate,
  hasTime,
  hasCity,
}: CompatSideData): CompatPrecision => {
  if (!hasDate) return 'sign_only'
  if (!hasTime) return 'date_only'
  return hasCity ? 'exact' : 'time_no_city'
}

/** Худшая из двух точностей — ею описывается пара целиком. */
export const worseCompatPrecision = (a: CompatPrecision, b: CompatPrecision): CompatPrecision =>
  PRECISION_RANK[a] >= PRECISION_RANK[b] ? a : b

/**
 * Асцендент входит в расчёт, только когда точный момент известен для ОБОИХ.
 *
 * Он не планета, а точка горизонта: она смещается на ~1° за четыре минуты и полностью зависит
 * от координат места. Посчитать его «примерно» невозможно — ошибка сразу в знак. Поэтому
 * правило одностороннее и жёсткое: либо у обоих дата, время и город, либо асцендента в расчёте
 * нет ни у кого (иначе процент зависел бы от того, чьи данные полнее, а не от пары).
 */
export const canUseAscendant = (own: CompatPrecision, target: CompatPrecision): boolean =>
  own === 'exact' && target === 'exact'

/**
 * Считаются ли точные углы между картами. Без полной даты второго градусов нет вовсе —
 * остаётся только сравнение знаков.
 */
export const hasCompatAspects = (own: CompatPrecision, target: CompatPrecision): boolean =>
  own !== 'sign_only' && target !== 'sign_only'
